import { call, put, PutEffect, select } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import api from '../../../../../api/_legacy'
import { ApiTradingAccount, ApiUnapprovedAccount } from '../../../../../api/_legacy/generated-api'
import { TENANT_ID } from '../../../constants'
import { getDateOnlyAsJson } from '../../../util/get-date-only-as-json'
import { FundingSource, TermsStepState } from '../../02-terms/state/terms-slice'
import { actions, WizardState } from './builder-slice'
import { mapStateToAccount, mapStateToAccountUpdate } from './mappers'

function raiseError(description: string) {
  throw new Error(description)
}

function validateAccountBeforeSave(builder: WizardState, terms: TermsStepState) {
  if (!terms.terms?.accountName) {
    raiseError('Invariant error: terms.accountName must be set')
  }

  if (!builder?.note?.noteId) {
    raiseError('Invariant error: noteId must be set')
  }

  if (!builder.client) {
    raiseError('Invariant error: note.client must be set')
  }

  if (!builder.client?.defaultCashInstruments?.depositId) {
    raiseError('Invariant error: note.client.defaultCashInstruments.depositId must be set')
  }

  if (!builder.client?.defaultCashInstruments?.loanId) {
    raiseError('Invariant error: note.client.defaultCashInstruments.loanId must be set')
  }
}

function createAccount(builder: WizardState, terms: TermsStepState): ApiUnapprovedAccount {
  validateAccountBeforeSave(builder, terms)

  const account: ApiUnapprovedAccount = {
    accountName: terms.terms.accountName,
    requestedStartDate: getDateOnlyAsJson(terms.terms.requestedStartDate),
    noteId: builder.note.noteId,
    depositId: builder.client?.defaultCashInstruments?.depositId,
    loanId: builder.client.defaultCashInstruments.loanId
  }

  switch (terms.terms.fundingSource) {
    case FundingSource.Transfer:
      return {
        ...account,
        transferUnits: []
      }
    case FundingSource.Cash:
      return {
        ...account,
        investment: Number(terms.terms.investment)
      }
    case FundingSource.Switch:
      return {
        ...account,
        switchRequest: {
          originatingAccountId: terms.terms.switchTrade.accountId,
          isFullSwitch: terms.terms.fullSwitch,
          switchAmount: terms.terms.fullSwitch ? null : Number(terms.terms.switchInvestment),
          isProcessed: null
        }
      }
  }
}

export function saveAccountForFirstTimeSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveTradeSaga() {
    const state: RootState = yield select()
    const { builder, terms } = state

    try {
      const account = createAccount(builder, terms)

      const result = yield call(api.tenant.saveCreate, TENANT_ID, account)

      yield put(actions.setAccountId(result.data.newAccountId))

      yield onSuccess
    } catch (error) {
      yield onFail(error.error)
    }
  }
}

export function saveAccountSaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveTradeSaga() {
    const state: RootState = yield select()
    const { builder, note, portfolio, strategy, terms } = state
    const { accountId } = builder

    if (!accountId) {
      throw new Error('FATAL ERROR: accountId has not been set')
    }

    try {
      const account = mapStateToAccount(accountId, note, terms, portfolio, strategy)

      yield call(api.tenant.saveCreate, TENANT_ID, account)

      if (state.builder.previousStep) {
        yield put(actions.moveToPreviousNavigatedStep())
      } else {
        yield onSuccess
      }
    } catch (error) {
      yield onFail(error)
    }
  }
}

export function updateAccountSaga(onSuccess: (e: any) => PutEffect, onFail: (e: any) => PutEffect) {
  return function* updateTradeSaga() {
    try {
      const state: RootState = yield select()
      const { builder, portfolio, strategy, topupWithdraw } = state
      const { accountId } = builder
      if (!accountId) {
        throw new Error('FATAL ERROR: accountId has not been set')
      }

      try {
        const request = mapStateToAccountUpdate(accountId, topupWithdraw, portfolio, strategy)

        const updatedResponse = yield call(api.tenant.updateSaveCreate, TENANT_ID, request as any)
        const updatedAccount = updatedResponse?.data?.updatedAccount as ApiTradingAccount
        yield onSuccess(updatedAccount)
      } catch (error) {
        yield onFail(error)
      }
    } catch (error) {
      yield onFail(error)
    }
  }
}
